import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Plus, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useEventCart } from "@/context/EventCartContext";
import { toast } from "sonner";
import Footer from "@/components/Footer";
import SponsorBanner from "@/components/SponsorBanner";

export default function Artists() {
  const [artists, setArtists] = useState([]);
  const [q, setQ] = useState("");
  const [disc, setDisc] = useState("");
  const [disciplines, setDisciplines] = useState([]);
  const [loading, setLoading] = useState(true);
  const { items, addArtist, removeArtist } = useEventCart();

  useEffect(() => {
    api.get("/disciplines").then((r) => setDisciplines(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    api
      .get("/artists", { params: { q, discipline: disc } })
      .then((r) => setArtists(r.data))
      .finally(() => setLoading(false));
  }, [q, disc]);

  const isSelected = (id) => items.artists.some((a) => a.id === id);

  return (
    <div className="pt-28 pb-16">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <span className="av-eyebrow">Catalogo Artisti</span>
        <h1 className="font-serif-display text-4xl sm:text-6xl mt-4 leading-tight">
          Talenti scelti per <span className="italic text-[#E5A93C]">accendere</span> la
          tua serata.
        </h1>

        <div
          className="mt-10 flex flex-wrap gap-3 items-center"
          data-testid="artists-filters"
        >
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A39E93]"
            />
            <input
              className="av-input pl-9"
              placeholder="Cerca per nome o città"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="artists-search-input"
            />
          </div>
          <select
            className="av-input max-w-xs"
            value={disc}
            onChange={(e) => setDisc(e.target.value)}
            data-testid="artists-discipline-select"
          >
            <option value="">Tutte le discipline</option>
            {disciplines.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <p className="text-[#A39E93] mt-16">Caricamento…</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mt-10">
            {artists.map((a) => {
              const selected = isSelected(a.id);
              return (
                <article
                  key={a.id}
                  className="av-card overflow-hidden group"
                  data-testid={`artist-card-${a.id}`}
                >
                  <Link to={`/artisti/${a.id}`} className="block" data-testid={`artist-link-${a.id}`}>
                    <div className="relative aspect-[4/3] overflow-hidden">
                      {a.image_url ? (
                        <img
                          src={a.image_url}
                          alt={a.name}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full bg-[#1d1a17]" />
                      )}
                      <div className="absolute top-3 left-3 av-eyebrow text-[10px]">
                        {a.discipline}
                      </div>
                    </div>
                  </Link>
                  <div className="p-5">
                    <Link to={`/artisti/${a.id}`}>
                      <h3 className="font-serif-display text-2xl hover:text-[#E5A93C] transition-colors">
                        {a.name}
                      </h3>
                    </Link>
                    <p className="text-sm text-[#A39E93] mt-1">{a.city}</p>
                    <p className="text-sm text-[#F7F4EE]/70 mt-3 line-clamp-3">
                      {a.bio}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-4">
                      {(a.tags || []).map((t) => (
                        <span
                          key={t}
                          className="text-[11px] px-2 py-1 rounded-full border av-hairline text-[#F7F4EE]/80"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between mt-5">
                      <div className="text-[#E5A93C] font-mono text-sm">
                        {a.price_from ? `da € ${a.price_from.toLocaleString()}` : "—"}
                      </div>
                      <button
                        className={`av-pill text-sm ${selected ? "av-pill-ghost" : "av-pill-gold"}`}
                        onClick={() => {
                          if (selected) {
                            removeArtist(a.id);
                            toast("Rimosso dalla bozza evento");
                          } else {
                            addArtist(a);
                            toast.success(`${a.name} aggiunto alla bozza`);
                          }
                        }}
                        data-testid={`artist-add-btn-${a.id}`}
                      >
                        {selected ? (
                          <>
                            <Check size={14} /> Selezionato
                          </>
                        ) : (
                          <>
                            <Plus size={14} /> Aggiungi
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
            {!artists.length && (
              <p className="text-[#A39E93] col-span-full">Nessun artista trovato.</p>
            )}
          </div>
        )}
      </section>
      <div className="mt-16">
        <SponsorBanner slot="artists" />
      </div>
      <Footer />
    </div>
  );
}
